/**
 * React.lazy that survives a deploy.
 *
 * Each build rewrites dist/client from scratch (webpack `output.clean`) and CodeBuild ships a fresh
 * artifact, so the hashed chunks a page loaded before a deploy no longer exist on the server after
 * it. A tab left open then navigates, webpack requests a URL that now 404s, and React throws a
 * ChunkLoadError that takes down the whole route.
 *
 * Two failure modes, two answers:
 *   - transient (flaky mobile network, aborted request): retrying works, webpack clears its
 *     internal chunk registry on error so the next import() really re-requests the file;
 *   - the chunk is gone: no retry can help, only a reload can, since the SSR then returns fresh
 *     HTML pointing at the new hashes.
 */

import { lazy } from 'react';

const MAX_ATTEMPTS = 3;
const RETRY_DELAY_MS = 350;
const RELOAD_STORAGE_KEY = 'chunkLoadReloadedAt';
const RELOAD_COOLDOWN_MS = 30000;

// Guards against several lazy components failing at once and each asking for a reload.
let reloadRequested = false;

const wait = delay => new Promise(resolve => {
	setTimeout(resolve, delay);
});

// webpack tags both its JS and its CSS chunk failures with this name; the message checks cover
// native dynamic imports and older runtimes.
export const isChunkLoadError = error => {
	if(!error) {
		return false;
	}

	const message = `${error.message || ''}`;

	return error.name === 'ChunkLoadError'
		|| error.code === 'CSS_CHUNK_LOAD_FAILED'
		|| /Loading (CSS )?chunk \S+ failed/i.test(message)
		|| /error loading dynamically imported module/i.test(message);
};

// One reload per cooldown window. If the freshly served HTML fails the same way, the site is broken
// for a reason a reload will not fix, and trapping the visitor in a refresh loop is worse than
// showing them the error page.
const reloadOnce = () => {
	// In development a chunk that fails to load is a bug to look at, not a stale deploy.
	if(process.env.NODE_ENV === 'development') {
		return false;
	}

	if(reloadRequested) {
		return true;
	}

	try {
		const lastReloadAt = Number(window.sessionStorage.getItem(RELOAD_STORAGE_KEY)) || 0;

		if(Date.now() - lastReloadAt < RELOAD_COOLDOWN_MS) {
			return false;
		}

		window.sessionStorage.setItem(RELOAD_STORAGE_KEY, `${Date.now()}`);
	} catch {
		// sessionStorage can throw (private browsing, blocked storage). Reloading once is still the
		// right call; `reloadRequested` keeps it to a single attempt per page instance.
	}

	reloadRequested = true;
	window.location.reload();

	return true;
};

export function lazyWithRetry(factory) {
	return lazy(async() => {
		// routes.js is imported by the SSR renderer, where there is nothing to retry or reload.
		if(typeof window === 'undefined') {
			return factory();
		}

		let lastError = null;

		for(let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
			try {
				return await factory(); // eslint-disable-line no-await-in-loop
			} catch(error) {
				// Anything else (a genuine bug in the loaded module) belongs to the error boundary.
				if(!isChunkLoadError(error)) {
					throw error;
				}

				lastError = error;

				if(attempt < MAX_ATTEMPTS) {
					await wait(RETRY_DELAY_MS * attempt); // eslint-disable-line no-await-in-loop
				}
			}
		}

		// Out of retries: the chunk is gone, not merely unreachable. Only fresh HTML can fix it.
		if(reloadOnce()) {
			// Never resolves: the browser is navigating away, so the Suspense fallback stays on
			// screen instead of flashing the error page on the way out.
			return new Promise(() => {});
		}

		throw lastError;
	});
}

export default lazyWithRetry;
